// @flow
import React, { Fragment, PureComponent } from 'react';
import { connect } from 'react-redux';
import DenzelEvent from 'app/common/lib/DenzelEvent';
import HistoryNavigator from 'app/common/lib/HistoryNavigator';
import i18n from 'app/common/lib/i18n';
import NavigateData from 'app/common/lib/NavigateData';
import NuxCountryList from 'app/common/react/components/growth/activation/NuxLocaleCountryStep/NuxCountryList';
import NuxLanguageList from 'app/common/react/components/growth/activation/NuxLocaleCountryStep/NuxLanguageList';
import PropTypes from 'prop-types';
import storage from 'app/common/lib/Storage';
import type { Dispatch } from 'redux';
import type { State as ReduxState } from 'app/common/redux/reducers/types';
import { Experiment } from 'app/common/react/components/experiments';
import { track } from 'app/common/lib/react/analytics';
import setNuxProgress, { type NuxStep } from 'app/common/redux/actions/nuxProgress';
import {
  DEFAULT_COUNTRY,
  DEFAULT_LOCALE,
  NUX_COUNTRY_LOCALE_STEP_STATUS,
  SHOWED,
  COMPLETED,
} from 'app/common/react/components/growth/activation/NuxConst/NuxConst';
import { Box, Button, Heading } from 'gestalt';
import { updateUser, setUserState } from 'app/common/redux/actions/nux/updateUser';

type OwnProps = {|
  showTableOfContents: boolean,
  toggleLoadingIndicator: (on: boolean) => void,
  incrementNuxStep: () => void,
  isBizNux: boolean,
  isRenux: boolean,
  personalizationCopy: {
    headerText: string,
  },
|};

type DispatchProps = {|
  updateUserInfo: ({
    updateUserData: {
      country: ?string,
      locale: ?string,
    },
  }) => Promise<any>,
  setNuxLocaleProgress: (number) => void,
  dispatchSetNuxStepProgress: (NuxStep) => void,
|};

type StateProps = {|
  reduxCountry: string,
  reduxLocale: string,
|};

type Props = {| ...OwnProps, ...StateProps, ...DispatchProps |};

type State = {
  locale: string,
  country: string,
  swappingLanguage: boolean,
};

type Context = {
  country: string,
  locale: string,
};

export class NuxLocaleCountryStep extends PureComponent<Props, State> {
  state = {
    locale: this.props.reduxLocale || (this.context || {}).locale || DEFAULT_LOCALE,
    country: this.props.reduxCountry || (this.context || {}).country || DEFAULT_COUNTRY,
    swappingLanguage: false,
  };

  componentDidMount() {
    const { dispatchSetNuxStepProgress, isRenux, setNuxLocaleProgress } = this.props;
    if (!isRenux) {
      setNuxLocaleProgress(SHOWED);
    }

    dispatchSetNuxStepProgress('country');

    const stepSeen =
      storage.localStorage.getItem(PCONST.LOCALSTORAGE_SIGNUP_USER_LOCALE) ||
      storage.localStorage.getItem(PCONST.LOCALSTORAGE_SIGNUP_USER_COUNTRY);
    if (!stepSeen) {
      track(PCONST.thrift.EventType.VIEW, {
        view: PCONST.thrift.ViewType.ORIENTATION,
        viewParameter: PCONST.thrift.ViewParameterType.ORIENTATION_LOCALE_COUNTRY_STEP,
      });
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { toggleLoadingIndicator, isBizNux, updateUserInfo } = this.props;
    const { country: prevCountry, locale: prevLocale } = prevState;
    const { country, locale } = this.state;
    const updatedCountry = prevCountry !== country;
    const updatedLocale = prevLocale !== locale;

    if (updatedCountry || updatedLocale) {
      updateUserInfo({ updateUserData: { country, locale } }).then(() => {
        if (updatedCountry) {
          storage.localStorage.setItem(PCONST.LOCALSTORAGE_SIGNUP_USER_COUNTRY, country);
        }
        if (updatedLocale) {
          storage.localStorage.setItem(PCONST.LOCALSTORAGE_SIGNUP_USER_LOCALE, locale);
          const _navigateData = new NavigateData();
          _navigateData.evt = new DenzelEvent('', null, { forceRefresh: 1 });
          if (!isBizNux) {
            toggleLoadingIndicator(true);
          } else {
            this.setState({ swappingLanguage: true });
          }

          HistoryNavigator.navigate('/', _navigateData);
        }
      });
    }
  }

  onNextButtonClick = () => {
    const { incrementNuxStep, isRenux, updateUserInfo, setNuxLocaleProgress } = this.props;
    const { country, locale } = this.state;
    updateUserInfo({ updateUserData: { country, locale } }).then(() => {
      if (!isRenux) {
        setNuxLocaleProgress(COMPLETED);
      }

      track(PCONST.thrift.EventType.CLICK, {
        component: PCONST.thrift.ComponentType.BROWSER,
        element: PCONST.thrift.ElementType.LOCALE_COUNTRY_NEXT_BUTTON,
        view: PCONST.thrift.ViewType.ORIENTATION,
        viewParameter: PCONST.thrift.ViewParameterType.ORIENTATION_LOCALE_COUNTRY_STEP,
      });
      incrementNuxStep();
    });
  };

  context: Context;

  updateLocale = ({ value: locale }: { value: string }) => this.setState({ locale });
  updateCountry = ({ value: country }: { value: string }) => this.setState({ country });

  renderOnHomefeed = () => {
    const {
      personalizationCopy: { headerText },
    } = this.props;
    const { country, locale, swappingLanguage } = this.state;
    const buttonWidth = 350;

    return (
      <Fragment>
        <Heading size="sm" overflow="normal">
          {headerText}
        </Heading>
        <Box display="flex" justifyContent="center" marginBottom={10} marginTop={5}>
          <Box data-test-id="nux-locale-select" marginRight={3} width={buttonWidth}>
            <NuxLanguageList
              id="newUserLanguage"
              name="locale"
              onChange={this.updateLocale}
              value={locale}
            />
          </Box>
          <Box data-test-id="nux-country-select" marginLeft={3} width={buttonWidth}>
            <NuxCountryList
              id="newUserCountry"
              name="country"
              onChange={this.updateCountry}
              value={country}
            />
          </Box>
        </Box>
        <Box
          data-test-id="nux-locale-country-next-btn"
          direction="column"
          display="flex"
          flex="grow"
          justifyContent="center"
          padding={1}
        >
          <Button
            size="lg"
            color="red"
            text={i18n._('Next', 'go to next step')}
            onClick={this.onNextButtonClick}
            type="submit"
            disabled={swappingLanguage}
          />
        </Box>
      </Fragment>
    );
  };

  render() {
    const {
      personalizationCopy: { headerText },
    } = this.props;
    const { country, locale, swappingLanguage } = this.state;
    return (
      <Experiment name="web_homefeed_nux" activate={false}>
        {({ anyEnabled: inHomefeedExp }) =>
          inHomefeedExp ? (
            this.renderOnHomefeed()
          ) : (
            <Box display="flex" direction="column" height="100%" padding={8} width="100%">
              <Box
                alignItems="center"
                dangerouslySetInlineStyle={
                  //overrides left-justified multiline headings
                  { __style: { textAlign: 'center' } }
                }
                display="flex"
                direction="column"
                flex="grow"
                justifyContent="around"
                marginBottom={8}
              >
                <Heading size="sm">{headerText}</Heading>
                <Box width="100%">
                  <Box data-test-id="nux-locale-select">
                    <NuxLanguageList
                      id="newUserLanguage"
                      name="locale"
                      onChange={this.updateLocale}
                      value={locale}
                    />
                  </Box>
                  <Box marginTop={4} data-test-id="nux-country-select">
                    <NuxCountryList
                      id="newUserCountry"
                      name="country"
                      onChange={this.updateCountry}
                      value={country}
                    />
                  </Box>
                </Box>
              </Box>
              <Box bottom data-test-id="nux-locale-country-next-btn">
                <Button
                  size="lg"
                  color="red"
                  text={i18n._('Next', 'go to next step')}
                  onClick={this.onNextButtonClick}
                  type="submit"
                  disabled={swappingLanguage}
                />
              </Box>
            </Box>
          )
        }
      </Experiment>
    );
  }
}

NuxLocaleCountryStep.contextTypes = {
  country: PropTypes.string,
  locale: PropTypes.string,
};

const mapStateToProps = ({ viewer }: ReduxState): StateProps => ({
  reduxCountry: viewer.country ? viewer.country : '',
  reduxLocale: viewer.locale ? viewer.locale : '',
});

const mapDispatchToProps = (dispatch: Dispatch<any>, props: OwnProps): DispatchProps => ({
  updateUserInfo: (data) => dispatch(updateUser(data)),
  setNuxLocaleProgress: (progress) =>
    dispatch(setUserState(NUX_COUNTRY_LOCALE_STEP_STATUS, progress)),
  dispatchSetNuxStepProgress: (step: NuxStep) => dispatch(setNuxProgress(step)),
});

export default connect<Props, OwnProps, _, _, _, _>(
  mapStateToProps,
  mapDispatchToProps,
)(NuxLocaleCountryStep);
